import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-[#0A0E14] text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0E14]/90 backdrop-blur-xl shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-8 h-28 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/c0eeee1c67bbb9492f644f58ac3e6b4f.png" alt="ZABRACORE AI" className="h-24 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-12">
            <a href="#solutions" className="text-[15px] text-gray-300 hover:text-[#00D9FF] transition-colors duration-300 relative group">
              Solutions
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#industries" className="text-[15px] text-gray-300 hover:text-[#00D9FF] transition-colors duration-300 relative group">
              Industries
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#process" className="text-[15px] text-gray-300 hover:text-[#00D9FF] transition-colors duration-300 relative group">
              Process
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#contact" className="text-[15px] text-gray-300 hover:text-[#00D9FF] transition-colors duration-300 relative group">
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
            </a>
          </div>
          <a href="#contact" className="px-8 py-3 rounded-full border-2 border-[#00D9FF] text-white hover:bg-[#00D9FF] hover:text-black transition-all duration-300 text-[15px] font-medium whitespace-nowrap cursor-pointer">
            Book Strategy Call
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0E14] via-[#0D1520] to-[#0A0E14]"></div>
          <div className="absolute top-1/2 right-1/4 w-[800px] h-[800px] bg-[#00D9FF]/10 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, #00D9FF 1px, transparent 1px)',
              backgroundSize: '50px 50px',
              animation: 'drift 20s linear infinite'
            }}></div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-[1200px] mx-auto px-8 text-left pt-32">
          <div className="space-y-6">
            <div className="text-[#00D9FF] text-[12px] uppercase tracking-[2px] font-semibold">
              INSTALL INTELLIGENCE AT THE CORE
            </div>
            <h1 className="text-[72px] leading-[1.1] font-bold max-w-[900px]">
              Install Intelligence at the Core of Your Business
            </h1>
            <p className="text-[22px] text-gray-300 leading-[1.6] max-w-[680px]">
              ZABRACORE AI builds intelligent systems that automate operations, capture revenue, and elevate customer experience for service businesses.
            </p>
            <p className="text-[18px] text-[#00D9FF] italic font-serif">
              We don't sell tools. We install the AI core that runs your business.
            </p>
            <div className="flex gap-5 pt-6">
              <a href="#contact" className="px-10 py-4 bg-[#00D9FF] text-black rounded-[30px] text-[16px] font-semibold hover:shadow-[0_0_30px_rgba(0,217,255,0.5)] hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Book an AI Strategy Call
              </a>
              <a href="#solutions" className="px-10 py-4 border-2 border-white text-white rounded-[30px] text-[16px] font-semibold hover:bg-white hover:text-black hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Explore Our Solutions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="relative">
        <div className="bg-[#0F1419]/60 backdrop-blur-[30px] border-t border-[#00D9FF] h-[100px]">
          <div className="max-w-[1400px] mx-auto h-full grid grid-cols-4 divide-x divide-[#1A1F26]">
            <div className="flex flex-col items-center justify-center gap-3">
              <i className="ri-building-line text-[32px] text-[#00D9FF]"></i>
              <span className="text-[15px] font-medium tracking-[0.5px]">Built for Service Businesses</span>
            </div>
            <div className="flex flex-col items-center justify-center gap-3">
              <i className="ri-shield-check-line text-[32px] text-[#00D9FF]"></i>
              <span className="text-[15px] font-medium tracking-[0.5px]">Secure &amp; Scalable AI Infrastructure</span>
            </div>
            <div className="flex flex-col items-center justify-center gap-3">
              <i className="ri-settings-3-line text-[32px] text-[#00D9FF]"></i>
              <span className="text-[15px] font-medium tracking-[0.5px]">Designed for Real-World Operations</span>
            </div>
            <div className="flex flex-col items-center justify-center gap-3">
              <i className="ri-user-heart-line text-[32px] text-[#00D9FF]"></i>
              <span className="text-[15px] font-medium tracking-[0.5px]">Automation That Feels Human</span>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-[#0D1117] py-32">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-[#00D9FF] text-[14px] font-mono mb-10">/WHAT_WE_DO</div>
              <h2 className="text-[56px] leading-[1.2] font-bold mb-8">
                A Smarter Way to Scale Service Businesses
              </h2>
              <div className="space-y-6 text-[20px] text-gray-300 leading-[1.7]">
                <p>
                  Most businesses stack software. ZABRACORE AI <span className="text-[#00D9FF] font-semibold">installs intelligence</span>.
                </p>
                <p>
                  We deploy AI agents and automation systems that handle calls, qualify leads, book appointments, and follow up automatically—so your business <span className="text-[#00D9FF] font-semibold">runs faster</span> with less manual effort.
                </p>
              </div>
            </div>
            <div className="relative h-[500px] flex items-center justify-center">
              <div className="relative w-full h-full">
                {/* Central Node */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-[#00D9FF]/20 rounded-full border-2 border-[#00D9FF] flex items-center justify-center animate-pulse">
                  <div className="w-12 h-12 bg-[#00D9FF] rounded-full shadow-[0_0_40px_rgba(0,217,255,0.6)]"></div>
                </div>
                {/* Satellite Nodes */}
                {[0, 60, 120, 180, 240, 300].map((angle, i) => {
                  const radian = (angle * Math.PI) / 180;
                  const x = Math.cos(radian) * 180;
                  const y = Math.sin(radian) * 180;
                  return (
                    <div
                      key={i}
                      className="absolute top-1/2 left-1/2 w-16 h-16 bg-[#0A0E14] rounded-full border border-[#00D9FF]/40 flex items-center justify-center"
                      style={{
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                        animation: `float ${3 + i * 0.5}s ease-in-out infinite`
                      }}
                    >
                      <div className="w-6 h-6 bg-[#00D9FF]/60 rounded-full"></div>
                    </div>
                  );
                })}
                {/* Connection Lines */}
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  {[0, 60, 120, 180, 240, 300].map((angle, i) => {
                    const radian = (angle * Math.PI) / 180;
                    const x1 = 250;
                    const y1 = 250;
                    const x2 = 250 + Math.cos(radian) * 180;
                    const y2 = 250 + Math.sin(radian) * 180;
                    return (
                      <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="#00D9FF"
                        strokeWidth="1"
                        opacity="0.3"
                        strokeDasharray="5,5"
                      >
                        <animate
                          attributeName="stroke-dashoffset"
                          from="0"
                          to="10"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </line>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section id="industries" className="py-32 bg-[#0A0E14]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center mb-6">
              <div className="h-0.5 w-[120px] bg-[#00D9FF]"></div>
            </div>
            <h2 className="text-[48px] font-bold mb-5">WHO WE SERVE</h2>
            <p className="text-[18px] text-gray-400">AI systems built for your industry</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-[1200px] mx-auto">
            {/* Card 1 */}
            <div className="bg-[#12161D] rounded-[24px] p-12 relative overflow-hidden group hover:-translate-y-2 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(0,217,255,0.2)]">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#00D9FF] group-hover:w-2 transition-all duration-300"></div>
              <h3 className="text-[32px] font-bold mb-3">Home Services</h3>
              <p className="text-[16px] text-gray-400 italic mb-10">AI systems built for speed and conversion.</p>
              <ul className="space-y-5 mb-12">
                <li className="flex items-start gap-4 text-[18px]">
                  <div className="w-2 h-2 bg-[#00D9FF] rounded-full mt-2 flex-shrink-0"></div>
                  <span>Missed-call recovery</span>
                </li>
                <li className="flex items-start gap-4 text-[18px]">
                  <div className="w-2 h-2 bg-[#00D9FF] rounded-full mt-2 flex-shrink-0"></div>
                  <span>24/7 AI phone &amp; chat</span>
                </li>
                <li className="flex items-start gap-4 text-[18px]">
                  <div className="w-2 h-2 bg-[#00D9FF] rounded-full mt-2 flex-shrink-0"></div>
                  <span>Automated scheduling</span>
                </li>
              </ul>
              <div className="bg-[#00D9FF]/15 rounded-2xl h-[60px] flex items-center justify-center">
                <span className="text-[20px] font-bold">Outcome: More booked jobs.</span>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-[#12161D] rounded-[24px] p-12 relative overflow-hidden group hover:-translate-y-2 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(157,78,221,0.2)]">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#9D4EDD] group-hover:w-2 transition-all duration-300"></div>
              <h3 className="text-[32px] font-bold mb-3">Medical &amp; Wellness</h3>
              <p className="text-[16px] text-gray-400 italic mb-10">AI that enhances patient experience and trust.</p>
              <ul className="space-y-5 mb-12">
                <li className="flex items-start gap-4 text-[18px]">
                  <div className="w-2 h-2 bg-[#9D4EDD] rounded-full mt-2 flex-shrink-0"></div>
                  <span>Smart scheduling</span>
                </li>
                <li className="flex items-start gap-4 text-[18px]">
                  <div className="w-2 h-2 bg-[#9D4EDD] rounded-full mt-2 flex-shrink-0"></div>
                  <span>No-show reduction</span>
                </li>
                <li className="flex items-start gap-4 text-[18px]">
                  <div className="w-2 h-2 bg-[#9D4EDD] rounded-full mt-2 flex-shrink-0"></div>
                  <span>Patient reactivation</span>
                </li>
              </ul>
              <div className="bg-[#9D4EDD]/15 rounded-2xl h-[60px] flex items-center justify-center">
                <span className="text-[20px] font-bold">Outcome: Full calendars.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-32 bg-[#0A0E14]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="mb-20">
            <h2 className="text-[48px] font-bold mb-4">SOLUTIONS</h2>
            <div className="h-0.5 w-[80px] bg-[#00D9FF]"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { icon: 'ri-phone-line', title: 'AI Voice Agents' },
              { icon: 'ri-message-3-line', title: 'AI Chat Agents' },
              { icon: 'ri-phone-find-line', title: 'Missed-Call Recovery' },
              { icon: 'ri-calendar-check-line', title: 'Intelligent Scheduling' },
              { icon: 'ri-database-2-line', title: 'CRM & Automation Architecture' },
              { icon: 'ri-user-add-line', title: 'Customer Reactivation' },
              { icon: 'ri-star-line', title: 'Review Automation' },
              { icon: 'ri-bar-chart-box-line', title: 'AI Reporting' }
            ].map((solution, i) => (
              <div
                key={i}
                className="bg-[#0A0D12] border border-[#1C2128] rounded-2xl p-9 hover:border-[#00D9FF] hover:bg-[#00D9FF]/5 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,217,255,0.15)] transition-all duration-300 cursor-pointer group"
              >
                <i className={`${solution.icon} text-[48px] text-[#00D9FF] mb-6 block group-hover:scale-110 transition-transform duration-300`}></i>
                <h3 className="text-[22px] font-bold">{solution.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why ZABRACORE AI */}
      <section className="py-32 bg-[#0D1117]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { icon: 'ri-stack-line', title: 'Infrastructure-first AI', desc: 'Built on robust, scalable foundations that grow with your business needs.' },
              { icon: 'ri-lightbulb-line', title: 'Industry-aware systems', desc: 'Tailored intelligence that understands your specific business context and challenges.' },
              { icon: 'ri-heart-pulse-line', title: 'Human-aware automation', desc: 'AI that enhances customer relationships rather than replacing human connection.' },
              { icon: 'ri-rocket-line', title: 'Scalable for growth', desc: 'Systems designed to handle increasing complexity as your business expands.' }
            ].map((item, i) => (
              <div key={i} className="space-y-5" style={{ marginTop: `${i * 20}px` }}>
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${item.icon} text-[40px] text-[#00D9FF]`}></i>
                </div>
                <h3 className="text-[24px] font-bold">{item.title}</h3>
                <p className="text-[16px] text-gray-300 leading-[1.7]">
                  {item.desc.split(' ').map((word, idx) => 
                    ['infrastructure', 'intelligence', 'enhances', 'scalable'].some(kw => word.toLowerCase().includes(kw)) ? 
                    <span key={idx} className="text-[#00D9FF]">{word} </span> : 
                    <span key={idx}>{word} </span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-32 bg-[#0A0E14]">
        <div className="max-w-[1000px] mx-auto px-8">
          <div className="text-center mb-24">
            <h2 className="text-[44px] font-bold mb-5">OUR PROCESS</h2>
            <p className="text-[17px] text-gray-400">From discovery to deployment in 4 phases</p>
          </div>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#00D9FF] -translate-x-1/2"></div>
            
            <div className="space-y-24">
              {[
                { num: '01', title: 'Discovery & Mapping', desc: 'We analyze your current operations, identify bottlenecks, and map opportunities for AI integration.' },
                { num: '02', title: 'AI Core Design', desc: 'Custom AI architecture tailored to your business workflows, customer journey, and growth objectives.' },
                { num: '03', title: 'Deployment & Automation', desc: 'Seamless implementation of AI agents, automation systems, and integration with your existing tools.' },
                { num: '04', title: 'Optimization & Scale', desc: 'Continuous monitoring, refinement, and scaling to maximize ROI and operational efficiency.' }
              ].map((step, i) => (
                <div key={i} className={`flex items-center gap-16 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <div className="bg-[#0D1117] rounded-[20px] p-8 inline-block max-w-[400px]">
                      <h3 className="text-[26px] font-bold mb-4">{step.title}</h3>
                      <p className="text-[16px] text-gray-300 leading-[1.6]">{step.desc}</p>
                    </div>
                  </div>
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-full bg-[#0A0E14] border-[3px] border-[#00D9FF] flex items-center justify-center relative z-10">
                      <span className="text-[32px] font-bold">{step.num}</span>
                    </div>
                    <div className={`absolute top-1/2 -translate-y-1/2 w-16 h-0.5 bg-[#00D9FF] ${i % 2 === 0 ? 'left-full' : 'right-full'}`}></div>
                  </div>
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="contact" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0E14] via-[#0D1520] to-[#0A0E14]">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#00D9FF]/15 rounded-full blur-[150px]"></div>
        </div>
        
        {/* Corner Brackets */}
        <div className="absolute top-8 left-8 w-[120px] h-[120px] border-l-2 border-t-2 border-[#00D9FF]"></div>
        <div className="absolute top-8 right-8 w-[120px] h-[120px] border-r-2 border-t-2 border-[#00D9FF]"></div>
        <div className="absolute bottom-8 left-8 w-[120px] h-[120px] border-l-2 border-b-2 border-[#00D9FF]"></div>
        <div className="absolute bottom-8 right-8 w-[120px] h-[120px] border-r-2 border-b-2 border-[#00D9FF]"></div>

        <div className="relative z-10 text-center max-w-[800px] mx-auto px-8">
          <h2 className="text-[64px] font-bold mb-8">Ready to Install Intelligence?</h2>
          <p className="text-[22px] text-gray-300 leading-[1.7] mb-14 max-w-[680px] mx-auto">
            If your business depends on calls or appointments, AI should already be working for you.
          </p>
          <a href="mailto:contact@zabracoreai.com" className="inline-block px-16 py-5 bg-[#00D9FF] text-black rounded-[35px] text-[18px] font-bold hover:shadow-[0_12px_48px_rgba(0,217,255,0.4)] transition-all duration-300 animate-pulse-subtle whitespace-nowrap cursor-pointer">
            Book Your AI Strategy Call
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#08090C] border-t border-[#00D9FF]">
        <div className="max-w-[1400px] mx-auto px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center mb-6">
                <img src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/c0eeee1c67bbb9492f644f58ac3e6b4f.png" alt="ZABRACORE AI" className="h-20 w-auto" />
              </div>
              <p className="text-[15px] text-gray-400 italic mb-5">The Intelligent Core of Your Business</p>
              <a href="mailto:contact@zabracoreai.com" className="text-[15px] text-[#00D9FF] hover:underline cursor-pointer">
                contact@zabracoreai.com
              </a>
            </div>
            <div>
              <h4 className="text-[16px] font-semibold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <a href="#solutions" className="block text-[15px] text-gray-400 hover:text-[#00D9FF] transition-colors cursor-pointer">Solutions</a>
                <a href="#industries" className="block text-[15px] text-gray-400 hover:text-[#00D9FF] transition-colors cursor-pointer">Industries</a>
                <a href="#process" className="block text-[15px] text-gray-400 hover:text-[#00D9FF] transition-colors cursor-pointer">Process</a>
                <a href="#contact" className="block text-[15px] text-gray-400 hover:text-[#00D9FF] transition-colors cursor-pointer">Contact</a>
              </div>
            </div>
            <div>
              <h4 className="text-[16px] font-semibold mb-4">Connect</h4>
              <div className="flex gap-4">
                {['ri-linkedin-box-fill', 'ri-twitter-x-fill', 'ri-github-fill', 'ri-mail-fill'].map((icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#00D9FF] flex items-center justify-center text-[#00D9FF] hover:bg-[#00D9FF] hover:text-black transition-all duration-300 cursor-pointer"
                  >
                    <i className={`${icon} text-[20px]`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-[13px] text-gray-600">© 2025 ZABRACORE AI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes drift {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes float {
          0%, 100% { transform: translate(var(--x), var(--y)) translateY(0px); }
          50% { transform: translate(var(--x), var(--y)) translateY(-10px); }
        }
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}